import dayjs from 'dayjs';
import {
  fromEnumAsString, toEnumFromString,
} from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { cloneData, isSameData } from '../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { TicketSupplementTemplate } from './ticket-supplement-template.js';
import { TimeZone } from '../../time/timezone.js';
import { toSafeFilename } from '../../utility/string.js';

export enum TicketSupplementStatus {
  Live = 'LIVE',
  Aborted = 'ABORTED',
  Canceled = 'CANCELED',
  Refunded = 'REFUNDED',
  Scanned = 'SCANNED',
}

export class TicketSupplement extends DataModel {
  static readonly TYPE_ID: string = '5832eb8846614fbd9faa9237854359a3';

  private constructor(
    public readonly uniqueId: string,
    public readonly status: TicketSupplementStatus,
    public readonly template: TicketSupplementTemplate,
    public readonly ticketId: string,
    public readonly storagePath: string,
    public readonly cancelledDate: dayjs.Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly scanDate: dayjs.Dayjs | null,
    public readonly scannerId: string | null,
  ) {
    super();
  }

  get templateId(): string {
    return this.template.uniqueId;
  }

  get $typeId(): string {
    return TicketSupplement.TYPE_ID;
  }

  static create(params: {
    uniqueId: string,
    status: TicketSupplementStatus,
    template: TicketSupplementTemplate,
    ticketId: string,
    storagePath: string,
    cancelledDate: dayjs.Dayjs | null,
    cancelledBy: string | null,
    scanDate: dayjs.Dayjs | null,
    scannerId: string | null,
  }): TicketSupplement {
    return new TicketSupplement(
        params.uniqueId,
        params.status,
        params.template,
        params.ticketId,
        params.storagePath,
        params.cancelledDate,
        params.cancelledBy,
        params.scanDate,
        params.scannerId,
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): TicketSupplement {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      status: toEnumFromString(MapUtils.getString(map, '$status'), TicketSupplementStatus),
      template: context.fromData<TicketSupplementTemplate>(MapUtils.getRecord(map, 'template')),
      ticketId: MapUtils.getString(map, 'ticketId'),
      storagePath: MapUtils.getString(map, 'storagePath'),
      cancelledDate: context.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'cancelledDate')),
      cancelledBy: MapUtils.getStringOrNull(map, 'cancelledBy'),
      scanDate: context.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'scanDate')),
      scannerId: MapUtils.getStringOrNull(map, 'scannerId'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      $status: fromEnumAsString(this.status),
      uniqueId: this.uniqueId,
      template: context.toData(this.template),
      templateId: this.templateId,
      ticketId: this.ticketId,
      storagePath: this.storagePath,
      cancelledDate: context.encodeDateOrNull(this.cancelledDate),
      cancelledBy: this.cancelledBy,
      scanDate: context.encodeDateOrNull(this.scanDate),
      scannerId: this.scannerId,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    status = this.status,
    template = cloneData(this.template),
    ticketId = this.ticketId,
    storagePath = this.storagePath,
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    scanDate = TimeZone.cloneDateOrNull(this.scanDate),
    scannerId = this.scannerId,
  }: {
    uniqueId?: string,
    status?: TicketSupplementStatus,
    template?: TicketSupplementTemplate,
    ticketId?: string,
    storagePath?: string,
    cancelledDate?: dayjs.Dayjs | null,
    cancelledBy?: string | null,
    scanDate?: dayjs.Dayjs | null,
    scannerId?: string | null,
  } = {}): this {
    return TicketSupplement.create({
      uniqueId,
      status,
      template,
      ticketId,
      storagePath,
      cancelledDate,
      cancelledBy,
      scanDate,
      scannerId,
    }) as this;
  }

  maybeCopyAsCanceled(
      cancelledBy: string | null,
      cancelledDate: dayjs.Dayjs,
      isRefund: boolean,
  ): this {
    const isAlreadyCanceledOrRefunded = this.status === TicketSupplementStatus.Refunded ||
      (this.status === TicketSupplementStatus.Canceled && !isRefund);

    if(isAlreadyCanceledOrRefunded) {
      return this.clone();
    }

    return this.copyWith({
      status: isRefund ? TicketSupplementStatus.Refunded : TicketSupplementStatus.Canceled,
      cancelledDate: this.cancelledDate ?? TimeZone.cloneDateOrNull(cancelledDate),
      cancelledBy: this.cancelledBy ?? cancelledBy,
    });
  }

  copyAsCanceled(
      cancelledBy: string | null,
      cancelledDate: dayjs.Dayjs,
      isRefund: boolean,
  ): this {
    return this.copyWith({
      status: isRefund ? TicketSupplementStatus.Refunded : TicketSupplementStatus.Canceled,
      cancelledDate: this.cancelledDate ?? TimeZone.cloneDateOrNull(cancelledDate),
      cancelledBy: this.cancelledBy ?? cancelledBy,
    });
  }

  copyAsScanned({
    scanDate,
    scannerId = null,
  }: {
    scanDate: dayjs.Dayjs,
    scannerId?: string | null,
  }): this {
    return this.copyWith({
      status: TicketSupplementStatus.Scanned,
      scanDate,
      scannerId,
    });
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: TicketSupplementStatus.Aborted,
    });
  }

  isSame(other: this): boolean {
    return other.uniqueId === this.uniqueId &&
      other.status === this.status &&
      isSameData(other.template, this.template) &&
      other.ticketId === this.ticketId &&
      other.storagePath === this.storagePath &&
      other.scanDate === this.scanDate &&
      other.scannerId === this.scannerId;
  }

  /**
   * Generates the storage path for a standard ticket supplement.
   * @param {string} customerId The customer ID.
   * @param {string} orderId The order ID.
   * @param {string} ticketId The ticket ID.
   * @param {string} eventName The event name.
   * @return {string} The storage path.
   */
  static generateCustomerStoragePath({ customerId, orderId, ticketId, eventName }: {
      customerId: string,
      orderId: string,
      ticketId: string,
      eventName: string,
  }): string {
    const sanitizedEventName = toSafeFilename(eventName).substring(0, 30);
    const filename = `${sanitizedEventName}_${ticketId}_supplement`;

    return `user_accounts/${customerId}/orders/${orderId}/supplements/${filename}.pdf`;
  }

  /**
   * Generates the storage path for a guest ticket supplement.
   * @param {string} orderId The order ID.
   * @param {string} ticketId The ticket ID.
   * @param {string} businessId The business ID.
   * @param {string} eventName The event name.
   * @return {string} The storage path.
   */
  static generateGuestStoragePath({ orderId, ticketId, businessId, eventName }: {
      orderId: string,
      ticketId: string,
      businessId: string,
      eventName: string,
  }): string {
    const sanitizedEventName = toSafeFilename(eventName).substring(0, 30);
    const filename = `${sanitizedEventName}_${ticketId}_supplement`;

    return `business_profiles/${businessId}/orders/${orderId}/supplements/${filename}.pdf`;
  }
}

export class TicketSupplementBuilder extends DataModelBuilder<TicketSupplement> {
  build(context: SerializationContext, map: SerializedData): TicketSupplement {
    return TicketSupplement.fromMap(context, map);
  }
}
